import { ReactElement, ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { publishAIChatSceneEvent } from '@ui/events'
import useUIStore from 'src/state/stores/ui'

interface Props {
  guid: string
  children: ReactNode
}

/**
 * A planar element the agent names in an answer. Hovering it outlines the
 * element in the scene (published to the editor's `AIChatSceneBridge`);
 * clicking it adds the element to the marked ones (blue, kept after the
 * pointer leaves) or removes it again. It does not navigate.
 */
const ElementLink = ({ guid, children }: Props): ReactElement => {
  const { projectId } = useParams()
  const isMarked = useUIStore(state =>
    projectId ? !!state.aiChatMarkedElementGuidsByProject[projectId]?.includes(guid) : false,
  )
  const toggleAiChatMarkedElementGuid = useUIStore(state => state.toggleAiChatMarkedElementGuid)

  const focus = (focusedElementGuids: string[]) => {
    if (projectId) publishAIChatSceneEvent({ projectGuid: projectId, focusedElementGuids })
  }

  return (
    <Box
      component="span"
      role="button"
      aria-pressed={isMarked}
      onMouseEnter={() => focus([guid])}
      onMouseLeave={() => focus([])}
      onClick={() => {
        if (projectId) toggleAiChatMarkedElementGuid(projectId, guid)
      }}
      sx={{
        color: 'primary.main',
        cursor: 'pointer',
        fontWeight: isMarked ? 'bold' : undefined,
        textDecoration: 'underline',
        textDecorationStyle: isMarked ? 'solid' : 'dotted',
        textUnderlineOffset: '2px',
      }}
      data-cy="ai-chat-element-link"
    >
      {children}
    </Box>
  )
}

export default ElementLink
